/**
 * Archetype catalog — per metal×route, an ordered stage list (referencing
 * STAGE_LIBRARY) with parallel-count dims, inclusion predicates, and wiring.
 *
 * v1 ships archetype A (iron green-sand auto). B–H (ductile no-bake, steel
 * no-bake, Al HPDC, Al gravity/LPDC, investment, copper sand, zinc/Mg HPDC) are
 * added as sibling Archetype objects following this exact shape — see
 * docs/design/foundry-industry-study.md §3.
 */

import type { PortDef } from "../../../foundry-widgets/foundry-plant";
import type { Archetype, ArchetypeStage, BuilderAnswers, Metal, Route } from "./types";

/** No-bake archetypes reuse the canonical auto-pour (ENT_POUR), whose `in_moulds`
 *  port defaults to green_sand_mould. Override the pour's input ports so the mould
 *  inlet reads the no-bake class (matching the mould_coat → pour edge), keeping the
 *  port + edge cosmetically consistent. The molten inlet stays molten_iron here and
 *  is recoloured per-metal by generatePlant; inoculant is unchanged. */
const NO_BAKE_POUR_PORTS: PortDef[] = [
  { id: "in_metal", name: "molten_metal", materialClass: "molten_iron" },
  { id: "in_moulds", name: "moulds", materialClass: "no_bake_mould" },
  { id: "in_inoculant", name: "inoculant", materialClass: "alloy_material" },
];

/** Archetype A — iron, automatic green-sand line. Reuses the canonical defs so
 *  a default generate round-trips to the existing 15-station plant. */
export const ARCH_IRON_GREEN_SAND_AUTO: Archetype = {
  id: "iron-green-sand-auto",
  name: "Iron · automatic green-sand line",
  blurb:
    "An automatic grey iron green sand foundry: charge make-up, induction melting, a sand plant, DISA moulding, a pouring machine, cooling, shakeout, inspection by eye and by non-destructive testing (NDT), and a sand testing lab.",
  metals: ["grey_iron"],
  route: "green_sand_auto",
  defaultMeltType: "induction",
  defaultVolume: "high",
  productionUnitLabel: "moulding lines",
  defaultCounts: { meltFurnaces: 1, productionUnits: 1, coreMachines: 0, pourStations: 1 },
  uses: { counts: ["meltFurnaces", "productionUnits"], toggles: ["testLab"] },
  stages: [
    {
      stageKey: "charge",
      layer: 0,
      wires: [
        { to: "furnace", fromPort: "out_charge", toPort: "in_charge", materialClass: "ferrous_charge", label: "Furnace charge" },
      ],
    },
    {
      stageKey: "furnace",
      parallel: "meltFurnaces",
      layer: 1,
      wires: [
        { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
        { to: "pour", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Molten metal (ladle)" },
      ],
    },
    {
      stageKey: "sand",
      layer: 1,
      wires: [
        { to: "moisture", fromPort: "out_sand", toPort: "in_sample", materialClass: "green_sand", label: "Sand sample" },
        { to: "vccd", fromPort: "out_sand", toPort: "in_sample", materialClass: "green_sand", label: "Sand sample" },
        { to: "vud", fromPort: "out_sand", toPort: "in_sample", materialClass: "green_sand", label: "Sand sample" },
        { to: "vpd", fromPort: "out_sand", toPort: "in_sample", materialClass: "green_sand", label: "Sand sample" },
        { to: "moulding", fromPort: "out_sand", toPort: "in_sand", materialClass: "green_sand", label: "Mixed sand" },
      ],
    },
    { stageKey: "spectro", layer: 2 },
    { stageKey: "moisture", layer: 2, when: (a) => a.testLab },
    { stageKey: "vccd", layer: 2, when: (a) => a.testLab },
    { stageKey: "vud", layer: 2, when: (a) => a.testLab },
    { stageKey: "vpd", layer: 2, when: (a) => a.testLab },
    {
      stageKey: "moulding",
      parallel: "productionUnits",
      layer: 3,
      wires: [
        { to: "vmhd", fromPort: "out_moulds", toPort: "in_mould", materialClass: "green_sand_mould", label: "Mould sample" },
        { to: "pour", fromPort: "out_moulds", toPort: "in_moulds", materialClass: "green_sand_mould", label: "Moulds" },
      ],
    },
    { stageKey: "vmhd", layer: 3, when: (a) => a.testLab },
    {
      stageKey: "pour",
      parallel: "productionUnits",
      layer: 4,
      wires: [
        { to: "cooling", fromPort: "out_poured", toPort: "in_poured", materialClass: "poured_mould", label: "Poured moulds" },
      ],
    },
    {
      stageKey: "cooling",
      parallel: "productionUnits",
      layer: 5,
      wires: [
        { to: "shakeout", fromPort: "out_cooled", toPort: "in_cooled", materialClass: "cooled_mould", label: "Cooled moulds" },
      ],
    },
    {
      stageKey: "shakeout",
      parallel: "productionUnits",
      layer: 6,
      wires: [
        { to: "shot_blast", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Raw castings" },
        { to: "sand", fromPort: "out_sand", toPort: "in_return", materialClass: "return_sand", label: "Return sand", style: "dashed" },
      ],
    },
    {
      stageKey: "shot_blast",
      layer: 7,
      wires: [
        { to: "fettling", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Blasted castings" },
      ],
    },
    {
      stageKey: "fettling",
      layer: 8,
      wires: [
        { to: "qc", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Fettled castings" },
      ],
    },
    {
      stageKey: "qc",
      layer: 9,
      wires: [
        { to: "tensile", fromPort: "out_accepted", toPort: "in_bars", materialClass: "test_specimen", label: "Test bars" },
      ],
    },
    { stageKey: "tensile", layer: 9, when: (a) => a.testLab },
  ],
};

// ── B — Ductile iron, no-bake (jobbing / heavy) ──────────────────────────────
/** Wind hubs, heavy hydraulics. Induction melt → Mg-treat → inoculate →
 *  no-bake moulding (+ optional core shop) → pour → cool → shakeout →
 *  sand reclaim loop → shot blast → fettling → (heat-treat) → (machining) →
 *  QC/NDT + tensile. Study §3 row B, §4 Mg-treatment/inoculation/no-bake. */
export const ARCH_DUCTILE_NOBAKE: Archetype = {
  id: "ductile-nobake",
  name: "Ductile iron · no-bake (jobbing/heavy)",
  blurb:
    "A ductile iron no-bake foundry for jobbing and heavy castings: induction melting, magnesium treatment and inoculation, self-setting no-bake moulding with sand reclamation, shot blasting, fettling, heat treatment and machining if wanted, and a lab for non-destructive testing (NDT) and tensile tests.",
  metals: ["ductile_iron"],
  route: "no_bake",
  defaultMeltType: "induction",
  defaultVolume: "jobbing",
  productionUnitLabel: "moulding bays",
  defaultCounts: { meltFurnaces: 1, productionUnits: 1, coreMachines: 1, pourStations: 1 },
  uses: {
    counts: ["meltFurnaces", "productionUnits", "coreMachines"],
    toggles: ["coreShop", "heatTreat", "machining", "testLab"],
  },
  stages: [
    {
      stageKey: "charge",
      layer: 0,
      wires: [{ to: "furnace", fromPort: "out_charge", toPort: "in_charge", materialClass: "ferrous_charge", label: "Furnace charge" }],
    },
    {
      stageKey: "furnace",
      parallel: "meltFurnaces",
      layer: 1,
      wires: [
        { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
        { to: "mg_treatment", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Base metal" },
      ],
    },
    { stageKey: "spectro", layer: 2 },
    {
      stageKey: "mg_treatment",
      layer: 2,
      wires: [{ to: "inoculation", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Treated metal" }],
    },
    {
      stageKey: "inoculation",
      layer: 3,
      wires: [{ to: "pour", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Inoculated metal (ladle)" }],
    },
    {
      stageKey: "nobake_supply_yard",
      layer: 1,
      wires: [
        { to: "nobake_mixer", fromPort: "out_silica", toPort: "in_silica", materialClass: "silica_sand", label: "Silica" },
        { to: "nobake_mixer", fromPort: "out_resin", toPort: "in_resin", materialClass: "no_bake_resin", label: "Resin" },
        { to: "nobake_mixer", fromPort: "out_catalyst", toPort: "in_catalyst", materialClass: "no_bake_catalyst", label: "Catalyst" },
        { to: "sand_reclaim", fromPort: "out_silica", toPort: "in_makeup", materialClass: "silica_sand", label: "Make-up sand" },
      ],
    },
    {
      stageKey: "nobake_mixer",
      layer: 2,
      wires: [{ to: "nobake_mould", fromPort: "out_sand", toPort: "in_sand", materialClass: "green_sand", label: "No-bake sand" }],
    },
    {
      stageKey: "core",
      parallel: "coreMachines",
      when: (a) => a.coreShop,
      layer: 2,
      wires: [{ to: "nobake_mould", fromPort: "out_cores", toPort: "in_core", materialClass: "no_bake_mould", label: "Cores" }],
    },
    {
      stageKey: "nobake_mould",
      parallel: "productionUnits",
      layer: 3,
      wires: [{ to: "mould_coat", fromPort: "out_moulds", toPort: "in_mould", materialClass: "no_bake_mould", label: "Stripped moulds" }],
    },
    {
      stageKey: "mould_coat",
      layer: 4,
      wires: [{ to: "pour", fromPort: "out_mould", toPort: "in_moulds", materialClass: "no_bake_mould", label: "Coated moulds" }],
    },
    {
      stageKey: "pour",
      parallel: "productionUnits",
      layer: 5,
      override: { inputPorts: NO_BAKE_POUR_PORTS },
      wires: [{ to: "cooling", fromPort: "out_poured", toPort: "in_poured", materialClass: "poured_mould", label: "Poured moulds" }],
    },
    {
      stageKey: "cooling",
      parallel: "productionUnits",
      layer: 6,
      wires: [{ to: "shakeout", fromPort: "out_cooled", toPort: "in_cooled", materialClass: "cooled_mould", label: "Cooled moulds" }],
    },
    {
      stageKey: "shakeout",
      parallel: "productionUnits",
      layer: 7,
      wires: [
        { to: "shot_blast", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Raw castings" },
        { to: "sand_reclaim", fromPort: "out_sand", toPort: "in_spent", materialClass: "return_sand", label: "Spent sand", style: "dashed" },
      ],
    },
    {
      stageKey: "sand_reclaim",
      layer: 6,
      wires: [{ to: "nobake_mixer", fromPort: "out_reclaimed", toPort: "in_return", materialClass: "return_sand", label: "Reclaimed sand", style: "dashed" }],
    },
    {
      stageKey: "shot_blast",
      layer: 8,
      wires: [{ to: "fettling", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Blasted castings" }],
    },
    {
      stageKey: "fettling",
      layer: 9,
      wires: [
        { to: "heat_treat", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Fettled castings" },
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Fettled castings" },
        { to: "qc", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Fettled castings" },
      ],
    },
    {
      stageKey: "heat_treat",
      when: (a) => a.heatTreat,
      layer: 10,
      wires: [
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Heat-treated castings" },
        { to: "qc", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Heat-treated castings" },
      ],
    },
    {
      stageKey: "machining",
      when: (a) => a.machining,
      layer: 11,
      wires: [{ to: "qc", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Machined castings" }],
    },
    {
      stageKey: "qc",
      layer: 12,
      wires: [{ to: "tensile", fromPort: "out_accepted", toPort: "in_bars", materialClass: "test_specimen", label: "Test bars" }],
    },
    { stageKey: "tensile", layer: 12, when: (a) => a.testLab },
  ],
};

// ── C — Carbon / alloy steel, no-bake (jobbing) ──────────────────────────────
/** Valve bodies, mining/rail wear. EAF melt (+ ladle) → no-bake moulding +
 *  core → high-temp pour → cool → shakeout → sand reclaim → shot blast →
 *  fettling → heat-treat (normalise/Q&T, on by default) → machining → heavy
 *  NDT + tensile. Study §3 row C, §4 EAF / no-bake / heat-treat. */
export const ARCH_STEEL_NOBAKE: Archetype = {
  id: "steel-nobake",
  name: "Carbon / alloy steel · no-bake (jobbing)",
  blurb:
    "A steel no-bake jobbing foundry: electric arc furnace melting, heavily risered no-bake moulding with a core shop and sand reclamation, pouring at high temperature, shot blasting, fettling, heat treatment to normalise or to quench and temper, machining, and a large lab for non-destructive testing (NDT) and tensile tests.",
  metals: ["carbon_steel"],
  route: "no_bake",
  defaultMeltType: "eaf",
  defaultVolume: "jobbing",
  productionUnitLabel: "moulding bays",
  defaultCounts: { meltFurnaces: 1, productionUnits: 1, coreMachines: 1, pourStations: 1 },
  uses: {
    counts: ["meltFurnaces", "productionUnits", "coreMachines"],
    toggles: ["coreShop", "heatTreat", "machining", "testLab"],
  },
  stages: [
    {
      stageKey: "charge",
      layer: 0,
      wires: [{ to: "eaf", fromPort: "out_charge", toPort: "in_charge", materialClass: "ferrous_charge", label: "Steel charge" }],
    },
    {
      stageKey: "eaf",
      parallel: "meltFurnaces",
      layer: 1,
      wires: [
        { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
        { to: "holding", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Tapped steel" },
      ],
    },
    { stageKey: "spectro", layer: 2 },
    {
      stageKey: "holding",
      layer: 2,
      wires: [{ to: "pour", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Ladle metal" }],
    },
    {
      stageKey: "nobake_supply_yard",
      layer: 1,
      wires: [
        { to: "nobake_mixer", fromPort: "out_silica", toPort: "in_silica", materialClass: "silica_sand", label: "Silica" },
        { to: "nobake_mixer", fromPort: "out_resin", toPort: "in_resin", materialClass: "no_bake_resin", label: "Resin" },
        { to: "nobake_mixer", fromPort: "out_catalyst", toPort: "in_catalyst", materialClass: "no_bake_catalyst", label: "Catalyst" },
        { to: "sand_reclaim", fromPort: "out_silica", toPort: "in_makeup", materialClass: "silica_sand", label: "Make-up sand" },
      ],
    },
    {
      stageKey: "nobake_mixer",
      layer: 2,
      wires: [{ to: "nobake_mould", fromPort: "out_sand", toPort: "in_sand", materialClass: "green_sand", label: "No-bake sand" }],
    },
    {
      stageKey: "core",
      parallel: "coreMachines",
      when: (a) => a.coreShop,
      layer: 2,
      wires: [{ to: "nobake_mould", fromPort: "out_cores", toPort: "in_core", materialClass: "no_bake_mould", label: "Cores" }],
    },
    {
      stageKey: "nobake_mould",
      parallel: "productionUnits",
      layer: 3,
      wires: [{ to: "mould_coat", fromPort: "out_moulds", toPort: "in_mould", materialClass: "no_bake_mould", label: "Stripped moulds" }],
    },
    {
      stageKey: "mould_coat",
      layer: 4,
      wires: [{ to: "pour", fromPort: "out_mould", toPort: "in_moulds", materialClass: "no_bake_mould", label: "Coated moulds" }],
    },
    {
      stageKey: "pour",
      parallel: "productionUnits",
      layer: 5,
      override: {
        oneLine: "Pours the molten steel from a crane ladle into the coated moulds, at 1550 to 1650 °C.",
        inputPorts: NO_BAKE_POUR_PORTS,
        parameters: [{ name: "pour_temperature", unit: "°C", value: 1600, range: [1550, 1650], dataKind: "analog_scalar" }],
      },
      wires: [{ to: "cooling", fromPort: "out_poured", toPort: "in_poured", materialClass: "poured_mould", label: "Poured moulds" }],
    },
    {
      stageKey: "cooling",
      parallel: "productionUnits",
      layer: 6,
      wires: [{ to: "shakeout", fromPort: "out_cooled", toPort: "in_cooled", materialClass: "cooled_mould", label: "Cooled moulds" }],
    },
    {
      stageKey: "shakeout",
      parallel: "productionUnits",
      layer: 7,
      wires: [
        { to: "shot_blast", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Raw castings" },
        { to: "sand_reclaim", fromPort: "out_sand", toPort: "in_spent", materialClass: "return_sand", label: "Spent sand", style: "dashed" },
      ],
    },
    {
      stageKey: "sand_reclaim",
      layer: 6,
      wires: [{ to: "nobake_mixer", fromPort: "out_reclaimed", toPort: "in_return", materialClass: "return_sand", label: "Reclaimed sand", style: "dashed" }],
    },
    {
      stageKey: "shot_blast",
      layer: 8,
      wires: [{ to: "fettling", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Blasted castings" }],
    },
    {
      stageKey: "fettling",
      layer: 9,
      wires: [{ to: "heat_treat", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Degated castings" }],
    },
    {
      stageKey: "heat_treat",
      when: (a) => a.heatTreat,
      layer: 10,
      override: { oneLine: "A heat treatment furnace that normalises, or quenches and tempers, the steel castings." },
      wires: [
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Heat-treated castings" },
        { to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Heat-treated castings" },
      ],
    },
    {
      stageKey: "machining",
      when: (a) => a.machining,
      layer: 11,
      wires: [{ to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Machined castings" }],
    },
    {
      stageKey: "ndt",
      layer: 12,
      override: { oneLine: "A lab that inspects steel castings by non-destructive testing (NDT): radiography, ultrasound and magnetic particles." },
      wires: [{ to: "tensile", fromPort: "out_accepted", toPort: "in_bars", materialClass: "test_specimen", label: "Test bars" }],
    },
    { stageKey: "tensile", layer: 12, when: (a) => a.testLab },
  ],
};

// ── D — Aluminium HPDC (mass) ────────────────────────────────────────────────
/** EV/automotive structural + powertrain. Reverb/dosing melt → degas →
 *  HPDC machines (parallel) → trim → shot blast/deburr → (T5/T6 heat-treat) →
 *  (machining) → X-ray/leak QC. No sand plant, no moulding line, no shakeout.
 *  Study §3 row D, §4 HPDC. */
export const ARCH_AL_HPDC: Archetype = {
  id: "al-hpdc",
  name: "Aluminium · high-pressure die casting (mass)",
  blurb:
    "An aluminium high pressure die casting cell for mass production: a reverberatory furnace and rotary degassing feed die casting machines side by side, with die spray, a trim press, deburring and shot blasting, T5 or T6 heat treatment and machining if wanted, and a quality lab with X-ray and leak tests. There is no sand plant.",
  metals: ["aluminium"],
  route: "hpdc",
  defaultMeltType: "reverb",
  defaultVolume: "high",
  productionUnitLabel: "die-casting machines",
  defaultCounts: { meltFurnaces: 1, productionUnits: 2, coreMachines: 0, pourStations: 0 },
  uses: { counts: ["meltFurnaces", "productionUnits"], toggles: ["heatTreat", "machining", "testLab"] },
  stages: [
    {
      stageKey: "reverb",
      parallel: "meltFurnaces",
      layer: 0,
      wires: [
        { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
        { to: "degassing", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Molten aluminium" },
      ],
    },
    { stageKey: "spectro", layer: 1, override: { oneLine: "An optical emission spectrometer that reads the aluminium alloy's silicon, copper, magnesium and iron." } },
    {
      stageKey: "degassing",
      layer: 1,
      wires: [{ to: "hpdc", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Degassed metal" }],
    },
    {
      stageKey: "hpdc",
      parallel: "productionUnits",
      layer: 2,
      wires: [
        { to: "die_spray", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Shots" },
        { to: "trim_press", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Shots" },
      ],
    },
    { stageKey: "die_spray", parallel: "productionUnits", layer: 3 },
    {
      stageKey: "trim_press",
      parallel: "productionUnits",
      layer: 4,
      wires: [{ to: "shot_blast", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Trimmed castings" }],
    },
    {
      stageKey: "shot_blast",
      layer: 5,
      override: { oneLine: "Deburrs the die castings by vibration and gives them a light shot blast." },
      wires: [
        { to: "heat_treat", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Deburred castings" },
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Deburred castings" },
        { to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Deburred castings" },
      ],
    },
    {
      stageKey: "heat_treat",
      when: (a) => a.heatTreat,
      layer: 6,
      override: {
        oneLine: "Solution treats the aluminium castings at 530 to 540 °C and ages them (T5 or T6).",
        parameters: [{ name: "furnace_temperature", unit: "°C", value: 535, range: [480, 545], dataKind: "analog_scalar" }],
      },
      wires: [
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "T6 castings" },
        { to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "T6 castings" },
      ],
    },
    {
      stageKey: "machining",
      when: (a) => a.machining,
      layer: 7,
      wires: [{ to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Machined castings" }],
    },
    {
      stageKey: "ndt",
      layer: 8,
      override: { oneLine: "Checks the castings for porosity by X-ray and for pressure tightness by a leak test." },
    },
  ],
};

// ── E — Aluminium gravity die / LPDC (mid) ───────────────────────────────────
/** Heads, wheels (LPDC). Crucible melt → degas (+ treatment) → gravity/LPDC
 *  casting stations (parallel) → cool → degate → shot blast → T6 heat-treat →
 *  machining → X-ray + RPT QC. No sand plant. Study §3 row E, §4 gravity/LPDC. */
export const ARCH_AL_GRAVITY: Archetype = {
  id: "al-gravity-lpdc",
  name: "Aluminium · gravity die / LPDC (mid)",
  blurb:
    "An aluminium gravity die or low pressure die foundry: crucible melting, degassing and treatment feed reusable die casting stations side by side, then cooling, degating, shot blasting, T6 heat treatment, machining, and a quality lab with X-ray and reduced pressure tests. There is no sand plant.",
  metals: ["aluminium"],
  route: "gravity_die",
  defaultMeltType: "crucible",
  defaultVolume: "mid",
  productionUnitLabel: "casting stations",
  defaultCounts: { meltFurnaces: 1, productionUnits: 2, coreMachines: 1, pourStations: 0 },
  uses: {
    counts: ["meltFurnaces", "productionUnits", "coreMachines"],
    toggles: ["coreShop", "heatTreat", "machining", "testLab"],
  },
  stages: [
    {
      stageKey: "crucible",
      parallel: "meltFurnaces",
      layer: 0,
      override: { oneLine: "A crucible furnace that melts the aluminium charge, at about 720 °C." },
      wires: [
        { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
        { to: "degassing", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Molten aluminium" },
      ],
    },
    { stageKey: "spectro", layer: 1, override: { oneLine: "An optical emission spectrometer that reads the aluminium alloy's silicon, copper, magnesium and iron." } },
    {
      stageKey: "degassing",
      layer: 1,
      wires: [{ to: "gravity_die", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Degassed metal" }],
    },
    {
      stageKey: "core",
      parallel: "coreMachines",
      when: (a) => a.coreShop,
      layer: 1,
      // Gravity/LPDC: the die is a PERMANENT reusable steel mould, so the die-insert
      // path is classed permanent_mould (override the shared cold-box core def, whose
      // default is the no-bake sand-core class no_bake_mould).
      override: { outputPorts: [{ id: "out_cores", name: "cores", materialClass: "permanent_mould" }] },
      wires: [{ to: "gravity_die", fromPort: "out_cores", toPort: "in_core", materialClass: "permanent_mould", label: "Sand cores" }],
    },
    {
      stageKey: "gravity_die",
      parallel: "productionUnits",
      layer: 2,
      wires: [{ to: "cooling", fromPort: "out_castings", toPort: "in_poured", materialClass: "poured_mould", label: "Castings cooling" }],
    },
    {
      stageKey: "cooling",
      parallel: "productionUnits",
      layer: 3,
      override: { oneLine: "Where the die castings solidify and cool evenly before the gates come off." },
      wires: [{ to: "fettling", fromPort: "out_cooled", toPort: "in_castings", materialClass: "raw_casting", label: "Cooled castings" }],
    },
    {
      stageKey: "fettling",
      layer: 4,
      override: { oneLine: "Saws or presses the gates and risers off the casting." },
      wires: [{ to: "shot_blast", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Degated castings" }],
    },
    {
      stageKey: "shot_blast",
      layer: 5,
      wires: [
        { to: "heat_treat", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Blasted castings" },
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Blasted castings" },
        { to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Blasted castings" },
      ],
    },
    {
      stageKey: "heat_treat",
      when: (a) => a.heatTreat,
      layer: 6,
      override: {
        oneLine: "Solution treats the aluminium castings at 530 to 540 °C and ages them (T6).",
        parameters: [{ name: "furnace_temperature", unit: "°C", value: 535, range: [480, 545], dataKind: "analog_scalar" }],
      },
      wires: [
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "T6 castings" },
        { to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "T6 castings" },
      ],
    },
    {
      stageKey: "machining",
      when: (a) => a.machining,
      layer: 7,
      wires: [{ to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Machined castings" }],
    },
    {
      stageKey: "ndt",
      layer: 8,
      override: { oneLine: "Checks the castings for porosity by X-ray and for soundness by a reduced pressure test." },
    },
  ],
};

// ── F — Investment casting (stainless / steel) ───────────────────────────────
/** Aero, medical, valves. Wax injection (parallel) → pattern assembly →
 *  shell build → dewax + fire → induction melt + pour → shell knockout →
 *  cut-off → fettling → solution heat-treat → CNC machining → RT/FPI/CMM QC.
 *  No sand plant. Study §3 row F, §2/§4 investment. */
export const ARCH_INVESTMENT: Archetype = {
  id: "investment-stainless",
  name: "Investment casting · stainless / steel (precision)",
  blurb:
    "A stainless or steel investment (lost wax) foundry: wax injection presses side by side, tree assembly, ceramic shell building, dewaxing and firing, induction melting and pouring, shell knockout, cut-off, fettling, solution heat treatment, CNC machining, and a quality lab with radiography, fluorescent penetrant and a coordinate measuring machine. There is no sand plant.",
  metals: ["stainless_steel"],
  route: "investment",
  defaultMeltType: "induction",
  defaultVolume: "mid",
  productionUnitLabel: "wax presses",
  defaultCounts: { meltFurnaces: 1, productionUnits: 2, coreMachines: 0, pourStations: 0 },
  uses: { counts: ["meltFurnaces", "productionUnits"], toggles: ["heatTreat", "machining", "testLab"] },
  stages: [
    {
      stageKey: "wax_injection",
      parallel: "productionUnits",
      layer: 0,
      wires: [{ to: "pattern_assembly", fromPort: "out_patterns", toPort: "in_patterns", materialClass: "pattern", label: "Wax patterns" }],
    },
    {
      stageKey: "pattern_assembly",
      layer: 1,
      wires: [{ to: "shell_build", fromPort: "out_tree", toPort: "in_tree", materialClass: "pattern", label: "Wax tree" }],
    },
    {
      stageKey: "shell_build",
      layer: 2,
      wires: [{ to: "dewax_fire", fromPort: "out_shell", toPort: "in_shell", materialClass: "investment_shell", label: "Green shell" }],
    },
    {
      stageKey: "dewax_fire",
      layer: 3,
      wires: [{ to: "investment_pour", fromPort: "out_shell", toPort: "in_shell", materialClass: "investment_shell", label: "Fired shell" }],
    },
    {
      stageKey: "furnace",
      parallel: "meltFurnaces",
      layer: 2,
      labelOverride: "Induction Melt",
      override: { oneLine: "An induction furnace that melts the stainless charge before it is refined in the AOD converter." },
      wires: [{ to: "aod", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Tapped steel" }],
    },
    {
      stageKey: "aod",
      layer: 3,
      override: { oneLine: "An argon oxygen decarburisation (AOD) converter that brings the stainless steel to low carbon and a tight chemistry." },
      wires: [
        { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
        { to: "investment_pour", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Refined metal" },
      ],
    },
    { stageKey: "spectro", layer: 4, override: { oneLine: "An optical emission spectrometer that reads the stainless steel's chromium, nickel, molybdenum and carbon." } },
    {
      stageKey: "investment_pour",
      layer: 4,
      wires: [{ to: "shell_knockout", fromPort: "out_poured", toPort: "in_poured", materialClass: "poured_mould", label: "Poured shells" }],
    },
    {
      stageKey: "shell_knockout",
      layer: 5,
      wires: [{ to: "cutoff", fromPort: "out_castings", toPort: "in_trees", materialClass: "raw_casting", label: "Cast trees" }],
    },
    {
      stageKey: "cutoff",
      layer: 6,
      wires: [{ to: "fettling", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Cut castings" }],
    },
    {
      stageKey: "fettling",
      layer: 7,
      override: { oneLine: "Belt grinds the gate contacts and blends them into the casting surface." },
      wires: [{ to: "heat_treat", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Fettled castings" }],
    },
    {
      stageKey: "heat_treat",
      when: (a) => a.heatTreat,
      layer: 8,
      override: {
        oneLine: "Solution anneals the stainless castings at about 1050 °C.",
        parameters: [{ name: "furnace_temperature", unit: "°C", value: 1050, range: [1000, 1100], dataKind: "analog_scalar" }],
      },
      wires: [
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Solution-treated castings" },
        { to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Solution-treated castings" },
      ],
    },
    {
      stageKey: "machining",
      when: (a) => a.machining,
      layer: 9,
      wires: [{ to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Machined castings" }],
    },
    {
      stageKey: "ndt",
      layer: 10,
      override: { oneLine: "Checks the castings by radiography and fluorescent penetrant, and measures them on a coordinate measuring machine." },
      wires: [{ to: "tensile", fromPort: "out_accepted", toPort: "in_bars", materialClass: "test_specimen", label: "Test bars" }],
    },
    { stageKey: "tensile", layer: 10, when: (a) => a.testLab },
  ],
};

// ── G — Copper-base sand (jobbing) ───────────────────────────────────────────
/** Valves, propellers, bushings. Crucible/induction melt → no-bake moulding +
 *  core → pour → cool → shakeout → sand reclaim → shot blast → fettling →
 *  machining → pressure-test + hardness QC. Study §3 row G, §2/§4 (sand variant). */
export const ARCH_COPPER_SAND: Archetype = {
  id: "copper-sand",
  name: "Copper-base · sand (jobbing)",
  blurb:
    "A copper-base sand jobbing foundry for bronze, gunmetal and leaded brass: crucible melting, no-bake moulding with a core shop and sand reclamation, gravity pouring, shakeout, shot blasting, fettling, machining, and a quality lab with pressure and hardness tests.",
  metals: ["copper_base"],
  route: "no_bake",
  defaultMeltType: "crucible",
  defaultVolume: "jobbing",
  productionUnitLabel: "moulding bays",
  defaultCounts: { meltFurnaces: 1, productionUnits: 1, coreMachines: 1, pourStations: 1 },
  uses: {
    counts: ["meltFurnaces", "productionUnits", "coreMachines"],
    toggles: ["coreShop", "machining", "testLab"],
  },
  stages: [
    {
      stageKey: "crucible",
      parallel: "meltFurnaces",
      layer: 0,
      override: {
        oneLine: "A crucible furnace that melts the copper-base charge at about 1150 °C, where zinc can fume off.",
        parameters: [{ name: "bath_temperature", unit: "°C", value: 1150, range: [1050, 1200], dataKind: "analog_scalar" }],
      },
      wires: [
        { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
        { to: "pour", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Molten metal (ladle)" },
      ],
    },
    { stageKey: "spectro", layer: 1, override: { oneLine: "An optical emission spectrometer that reads the copper alloy's copper, tin, zinc, lead and aluminium." } },
    {
      stageKey: "nobake_supply_yard",
      layer: 0,
      wires: [
        { to: "nobake_mixer", fromPort: "out_silica", toPort: "in_silica", materialClass: "silica_sand", label: "Silica" },
        { to: "nobake_mixer", fromPort: "out_resin", toPort: "in_resin", materialClass: "no_bake_resin", label: "Resin" },
        { to: "nobake_mixer", fromPort: "out_catalyst", toPort: "in_catalyst", materialClass: "no_bake_catalyst", label: "Catalyst" },
        { to: "sand_reclaim", fromPort: "out_silica", toPort: "in_makeup", materialClass: "silica_sand", label: "Make-up sand" },
      ],
    },
    {
      stageKey: "nobake_mixer",
      layer: 1,
      wires: [{ to: "nobake_mould", fromPort: "out_sand", toPort: "in_sand", materialClass: "green_sand", label: "No-bake sand" }],
    },
    {
      stageKey: "core",
      parallel: "coreMachines",
      when: (a) => a.coreShop,
      layer: 1,
      wires: [{ to: "nobake_mould", fromPort: "out_cores", toPort: "in_core", materialClass: "no_bake_mould", label: "Cores" }],
    },
    {
      stageKey: "nobake_mould",
      parallel: "productionUnits",
      layer: 2,
      wires: [{ to: "pour", fromPort: "out_moulds", toPort: "in_moulds", materialClass: "no_bake_mould", label: "Moulds" }],
    },
    {
      stageKey: "pour",
      parallel: "productionUnits",
      layer: 3,
      override: {
        oneLine: "Pours the molten copper-base alloy into the moulds by gravity, at about 1120 °C.",
        inputPorts: NO_BAKE_POUR_PORTS,
        parameters: [{ name: "pour_temperature", unit: "°C", value: 1120, range: [1050, 1180], dataKind: "analog_scalar" }],
      },
      wires: [{ to: "cooling", fromPort: "out_poured", toPort: "in_poured", materialClass: "poured_mould", label: "Poured moulds" }],
    },
    {
      stageKey: "cooling",
      parallel: "productionUnits",
      layer: 4,
      wires: [{ to: "shakeout", fromPort: "out_cooled", toPort: "in_cooled", materialClass: "cooled_mould", label: "Cooled moulds" }],
    },
    {
      stageKey: "shakeout",
      parallel: "productionUnits",
      layer: 5,
      wires: [
        { to: "shot_blast", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Raw castings" },
        { to: "sand_reclaim", fromPort: "out_sand", toPort: "in_spent", materialClass: "return_sand", label: "Spent sand", style: "dashed" },
      ],
    },
    {
      stageKey: "sand_reclaim",
      layer: 4,
      wires: [{ to: "nobake_mixer", fromPort: "out_reclaimed", toPort: "in_return", materialClass: "return_sand", label: "Reclaimed sand", style: "dashed" }],
    },
    {
      stageKey: "shot_blast",
      layer: 6,
      wires: [{ to: "fettling", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Blasted castings" }],
    },
    {
      stageKey: "fettling",
      layer: 7,
      wires: [
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Fettled castings" },
        { to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Fettled castings" },
      ],
    },
    {
      stageKey: "machining",
      when: (a) => a.machining,
      layer: 8,
      wires: [{ to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Machined castings" }],
    },
    {
      stageKey: "ndt",
      layer: 9,
      override: { oneLine: "Checks the castings for pressure tightness by a leak test and for Brinell hardness." },
    },
  ],
};

// ── H — Zinc / Mg hot-chamber HPDC ───────────────────────────────────────────
/** Hardware, trim, electronics. On-machine melt pot → HPDC machines (parallel)
 *  → trim → shot blast/deburr → (plating, external) → machining → dimensional +
 *  leak QC. No melt yard / sand plant. Study §3 row H, §1/§4 (hot-chamber HPDC). */
export const ARCH_ZNMG_HPDC: Archetype = {
  id: "znmg-hpdc",
  name: "Zinc / Mg · hot-chamber HPDC",
  blurb:
    "A zinc or magnesium hot chamber die casting cell: melt pots on each machine feed die casting machines side by side, with die spray, a trim press, deburring and shot blasting, machining if wanted, and a quality lab for dimensions and leak tests. Plating is done outside, and magnesium runs under a cover gas.",
  metals: ["zinc", "magnesium"],
  route: "hpdc",
  defaultMeltType: "crucible",
  defaultVolume: "high",
  productionUnitLabel: "die-casting machines",
  defaultCounts: { meltFurnaces: 0, productionUnits: 2, coreMachines: 0, pourStations: 0 },
  uses: { counts: ["productionUnits"], toggles: ["machining", "testLab"] },
  stages: [
    {
      stageKey: "crucible",
      parallel: "productionUnits",
      layer: 0,
      labelOverride: "On-machine Melt Pot",
      override: {
        oneLine: "The melt pot built into a hot chamber die casting machine: zinc at about 415 °C, or magnesium under a cover gas.",
        parameters: [{ name: "bath_temperature", unit: "°C", value: 415, range: [380, 430], dataKind: "analog_scalar" }],
      },
      wires: [{ to: "hpdc", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Molten metal" }],
    },
    {
      stageKey: "hpdc",
      parallel: "productionUnits",
      layer: 1,
      override: {
        oneLine: "A hot chamber high pressure die casting machine that injects zinc or magnesium into a steel die through a gooseneck.",
        parameters: [
          { name: "melt_temperature", unit: "°C", value: 415, range: [400, 430], dataKind: "analog_scalar" },
          { name: "die_temperature", unit: "°C", value: 175, range: [150, 200], dataKind: "analog_scalar" },
          { name: "intensification_pressure", unit: "MPa", value: 25, range: [10, 40], dataKind: "analog_scalar" },
          { name: "plunger_velocity", unit: "m/s", value: 2, range: [1, 3], dataKind: "analog_scalar" },
        ],
      },
      wires: [
        { to: "die_spray", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Shots" },
        { to: "trim_press", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Shots" },
      ],
    },
    { stageKey: "die_spray", parallel: "productionUnits", layer: 2 },
    {
      stageKey: "trim_press",
      parallel: "productionUnits",
      layer: 3,
      wires: [{ to: "shot_blast", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Trimmed castings" }],
    },
    {
      stageKey: "shot_blast",
      layer: 4,
      override: { oneLine: "Deburrs the castings by vibration and gives them a light shot blast before plating." },
      wires: [
        { to: "machining", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Deburred castings" },
        { to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Deburred castings" },
      ],
    },
    {
      stageKey: "machining",
      when: (a) => a.machining,
      layer: 5,
      wires: [{ to: "ndt", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Machined castings" }],
    },
    {
      stageKey: "ndt",
      layer: 6,
      override: { oneLine: "Measures the castings on a coordinate measuring machine and checks them by a pressure leak test." },
    },
  ],
};

// ── Standard Green-Sand Foundry (STD archetype: grey + ductile) ──────────────
/**
 * The authored ~35-station "Standard Green-Sand Foundry" showcase plant. Two
 * sibling archetypes share one route (`green_sand_standard`) and resolve by
 * metal: grey-iron (plain holding, no Mg) vs ductile/SG (Mg treatment +
 * inoculation, nodularity at final inspection).
 *
 * Four spines + feedback loops + measurement leaves, per the approved plan's
 * "Connections / wiring" section:
 *   metal: Charge → Furnace → [Treatment(ductile)] → Inoculation → Pour
 *   sand:  Raw Store → Batch Hopper → Muller → Prepared Bunker → Moulding Hopper
 *          → Moulding → Core Setting → Pour → Cooling → Shakeout
 *   cast:  Shakeout → De-gating → Shot Blasting → Fettling → Final Inspection
 *          → Finished Casting Store
 *   core:  Raw Core Sand Store + Resin Store → Core Sand Mixer → Core Shooter
 *          → Knockout → Coating → Core Storage → Core Setting
 *   feedback (dashed): Shakeout → Polygon Sieve → Magnetic Separator →
 *          Sand Cooler → Return Silo → Muller; De-gating runners/risers +
 *          Final-Inspection rejects → Charge Prep; Sand Cooler → Dust Extractor
 *          → Dust Storage.
 *   leaves (sample edges): OES ← Furnace; VCAT ← Moulding Hopper; Sand Lab ←
 *          Muller + Prepared Bunker + Return Silo + Moulding Hopper; Versatile
 *          Moisture/VCCD/VUD/VPD ← Muller; VMHD ← Moulding; Core Lab ← Core
 *          Shooter; Tensile ← Final Inspection specimen; Compactability
 *          Controller ← Muller.
 *
 * Multi-input targets (Batch Hopper, Muller, Core Sand Mixer, Core Setting,
 * Sand Lab, Charge Prep) always carry an explicit `toPort` so generatePlant's
 * port resolver lands each edge on the intended inlet.
 */

/** Counts the STD archetypes surface (shared). */
const STD_USES: Archetype["uses"] = {
  counts: ["meltFurnaces", "productionUnits", "coreMachines"],
  toggles: ["coreShop", "testLab"],
};

/** Default per-line counts for the STD showcase plant (shared). */
const STD_COUNTS = { meltFurnaces: 1, productionUnits: 1, coreMachines: 1, pourStations: 1 };

/**
 * The sand plant, core shop, casting/finishing spines and measurement leaves —
 * identical for grey + ductile. A factory returns fresh arrays so the two
 * archetypes never share mutable stage objects. The melt-treatment head differs
 * per metal and is prepended by each archetype (see below).
 */
function stdCommonStages(): ArchetypeStage[] {
  return [
    // ── Sand plant head-end ──
    // std_raw_material_store is the sand system's ONE bought-material boundary
    // (#677's charge-yard pattern): every fresh input downstream — the batch
    // hopper's premix AND the small make-up trickles below — traces back here,
    // not a separate purchase point per stage. The make-up wires (#698) ground
    // the buffer/transport spine (bunker, hopper) and the reclaim loop (sieve,
    // separator, cooler, return silo): a real green-sand system loses sand to
    // evaporation, screen rejects, tramp ejection and storage sweep at EVERY
    // handling point, not only at the muller, and silica_sand's own classes.yaml
    // notes say exactly that ("Fresh make-up sand added to compensate for
    // losses" — general, not muller-specific). Water trim at the two buffers
    // mirrors the compactability controller's own established role (trims the
    // water dose); evaporative water at the cooler is what "evaporative cool"
    // (the entity's own oneLine) means.
    {
      stageKey: "std_raw_material_store",
      layer: 0,
      wires: [
        { to: "std_batch_hopper", fromPort: "out_silica", toPort: "in_silica", materialClass: "silica_sand", label: "Silica" },
        { to: "std_batch_hopper", fromPort: "out_bentonite", toPort: "in_bentonite", materialClass: "bentonite", label: "Bentonite" },
        { to: "std_batch_hopper", fromPort: "out_sea_coal", toPort: "in_sea_coal", materialClass: "sea_coal", label: "Sea coal" },
        { to: "std_batch_hopper", fromPort: "out_water", toPort: "in_water", materialClass: "water", label: "Water" },
        { to: "std_prepared_bunker", fromPort: "out_water", toPort: "in_trim_water", materialClass: "water", label: "Water trim", style: "dashed" },
        { to: "std_moulding_hopper", fromPort: "out_water", toPort: "in_trim_water", materialClass: "water", label: "Water trim", style: "dashed" },
        { to: "std_polygon_sieve", fromPort: "out_silica", toPort: "in_makeup", materialClass: "silica_sand", label: "Make-up sand", style: "dashed" },
        { to: "std_magnetic_separator", fromPort: "out_silica", toPort: "in_makeup", materialClass: "silica_sand", label: "Make-up sand", style: "dashed" },
        { to: "std_sand_cooler", fromPort: "out_water", toPort: "in_evap_water", materialClass: "water", label: "Evaporative water", style: "dashed" },
        { to: "std_return_silo", fromPort: "out_silica", toPort: "in_makeup", materialClass: "silica_sand", label: "Make-up sand", style: "dashed" },
      ],
    },
    {
      stageKey: "std_batch_hopper",
      layer: 1,
      wires: [
        { to: "sand", fromPort: "out_silica", toPort: "in_silica", materialClass: "silica_sand", label: "Silica" },
        { to: "sand", fromPort: "out_bentonite", toPort: "in_bentonite", materialClass: "bentonite", label: "Bentonite" },
        { to: "sand", fromPort: "out_sea_coal", toPort: "in_seacoal", materialClass: "sea_coal", label: "Sea coal" },
        { to: "sand", fromPort: "out_water", toPort: "in_water", materialClass: "water", label: "Water" },
      ],
    },
    {
      // Muller / intensive mixer (reused ENT_SAND_MIXER) — green-sand conditioner.
      stageKey: "sand",
      layer: 2,
      wires: [
        { to: "std_prepared_bunker", fromPort: "out_sand", toPort: "in_sand", materialClass: "green_sand", label: "Green sand" },
        // Sand-lab + Versatile + controller sample leaves off the muller — a
        // carve-out for destructive testing, not more of the recirculating
        // green_sand it was drawn from (#698; see ENT_STD_SAND_LAB's comment).
        { to: "std_sand_lab", fromPort: "out_sand", toPort: "in_muller", materialClass: "test_specimen", label: "Sand sample" },
        { to: "std_compactability_controller", fromPort: "out_sand", toPort: "in_sample", materialClass: "test_specimen", label: "Sand sample" },
        { to: "moisture", fromPort: "out_sand", toPort: "in_sample", materialClass: "test_specimen", label: "Sand sample" },
        { to: "vccd", fromPort: "out_sand", toPort: "in_sample", materialClass: "test_specimen", label: "Sand sample" },
        { to: "vud", fromPort: "out_sand", toPort: "in_sample", materialClass: "test_specimen", label: "Sand sample" },
        { to: "vpd", fromPort: "out_sand", toPort: "in_sample", materialClass: "test_specimen", label: "Sand sample" },
        { to: "vmbd", fromPort: "out_sand", toPort: "in_sample", materialClass: "test_specimen", label: "Sand sample" },
      ],
    },
    {
      stageKey: "std_prepared_bunker",
      layer: 3,
      wires: [
        { to: "std_moulding_hopper", fromPort: "out_sand", toPort: "in_sand", materialClass: "green_sand", label: "Green sand" },
        { to: "std_sand_lab", fromPort: "out_sand", toPort: "in_bunker", materialClass: "test_specimen", label: "Sand sample" },
      ],
    },
    {
      stageKey: "std_moulding_hopper",
      layer: 4,
      wires: [
        { to: "moulding", fromPort: "out_sand", toPort: "in_sand", materialClass: "green_sand", label: "Green sand" },
        { to: "std_vcat", fromPort: "out_sand", toPort: "in_sample", materialClass: "test_specimen", label: "Sand sample" },
        { to: "std_sand_lab", fromPort: "out_sand", toPort: "in_moulding", materialClass: "test_specimen", label: "Sand sample" },
      ],
    },
    { stageKey: "std_sand_lab", layer: 3, when: (a) => a.testLab },
    { stageKey: "std_vcat", layer: 5, when: (a) => a.testLab },
    { stageKey: "std_compactability_controller", layer: 3 },
    { stageKey: "moisture", layer: 3, when: (a) => a.testLab },
    { stageKey: "vccd", layer: 3, when: (a) => a.testLab },
    { stageKey: "vud", layer: 3, when: (a) => a.testLab },
    { stageKey: "vpd", layer: 3, when: (a) => a.testLab },
    { stageKey: "vmbd", layer: 3, when: (a) => a.testLab },

    // ── Core shop ──
    {
      stageKey: "std_core_sand_store",
      when: (a) => a.coreShop,
      layer: 0,
      wires: [{ to: "std_core_sand_mixer", fromPort: "out_sand", toPort: "in_sand", materialClass: "core_sand", label: "Core sand" }],
    },
    {
      stageKey: "std_resin_store",
      when: (a) => a.coreShop,
      layer: 0,
      wires: [{ to: "std_core_sand_mixer", fromPort: "out_resin", toPort: "in_resin", materialClass: "resin_pur", label: "PUR resin" }],
    },
    {
      stageKey: "std_core_sand_mixer",
      when: (a) => a.coreShop,
      layer: 1,
      wires: [{ to: "std_core_shooter", fromPort: "out_mix", toPort: "in_sand", materialClass: "core_sand", label: "Core mix" }],
    },
    {
      stageKey: "std_core_shooter",
      parallel: "coreMachines",
      when: (a) => a.coreShop,
      layer: 2,
      wires: [
        { to: "std_core_knockout", fromPort: "out_core", toPort: "in_core", materialClass: "cold_box_core", label: "Cores" },
        { to: "std_core_lab", fromPort: "out_core", toPort: "in_sample", materialClass: "cold_box_core", label: "Core sample" },
        // The VCH reads a CURED core, which is why it hangs off the shooter and not
        // the sand mixer: core_sand is loose sand, with nothing to indent until the
        // shooter cures it.
        { to: "vch", fromPort: "out_core", toPort: "in_core", materialClass: "cold_box_core", label: "Core sample" },
      ],
    },
    {
      stageKey: "std_core_knockout",
      when: (a) => a.coreShop,
      layer: 3,
      wires: [{ to: "std_core_coating", fromPort: "out_core", toPort: "in_core", materialClass: "cold_box_core", label: "Cores" }],
    },
    {
      stageKey: "std_core_coating",
      when: (a) => a.coreShop,
      layer: 4,
      wires: [{ to: "std_core_storage", fromPort: "out_core", toPort: "in_core", materialClass: "cold_box_core", label: "Coated cores" }],
    },
    {
      stageKey: "std_core_storage",
      when: (a) => a.coreShop,
      layer: 5,
      wires: [{ to: "std_core_setting", fromPort: "out_core", toPort: "in_core", materialClass: "cold_box_core", label: "Cores" }],
    },
    { stageKey: "std_core_lab", when: (a) => a.coreShop && a.testLab, layer: 3 },
    // VCH must gate on coreShop, not just testLab: without a core shop the shooter
    // stage is excluded, generatePlant drops the wire (no target), and the probe
    // renders as an ORPHAN node with no input — generatePlant does not prune those.
    // `parallel` is not optional either: the shooter is parallel="coreMachines", so
    // an unparallelised probe would fan BOTH shooters into one node ⇒ one tag ⇒ both
    // machines sharing a single readings series.
    { stageKey: "vch", parallel: "coreMachines", when: (a) => a.coreShop, layer: 3 },

    // ── Mould assembly → pour → cool → shakeout ──
    // Pattern shop — same layer as moulding (a supplier column, not upstream of
    // it), the established pattern for a sibling source stage (cf. charge_yard
    // alongside charge; #698 — ENT_MOULDING's own in_pattern port existed all
    // along, it was simply never wired here).
    {
      stageKey: "std_pattern_shop",
      layer: 5,
      wires: [{ to: "moulding", fromPort: "out_pattern", toPort: "in_pattern", materialClass: "pattern", label: "Pattern" }],
    },
    {
      // Moulding (reused ENT_MOULDING) — green sand + pattern → green-sand mould.
      stageKey: "moulding",
      parallel: "productionUnits",
      layer: 5,
      wires: [
        { to: "std_core_setting", fromPort: "out_moulds", toPort: "in_mould", materialClass: "green_sand_mould", label: "Moulds" },
        { to: "vmhd", fromPort: "out_moulds", toPort: "in_mould", materialClass: "green_sand_mould", label: "Mould sample" },
        // The handhelds read an UNPOURED mould — once metal is in the cavity you are
        // not reading the sand surface any more, which is why they hang here and not
        // off cooling/shakeout.
        { to: "vmh", fromPort: "out_moulds", toPort: "in_mould", materialClass: "green_sand_mould", label: "Mould sample" },
        { to: "vms", fromPort: "out_moulds", toPort: "in_mould", materialClass: "green_sand_mould", label: "Mould sample" },
      ],
    },
    { stageKey: "vmhd", layer: 5, when: (a) => a.testLab },
    // Ungated on purpose: a handheld is a LINE tool, not a lab bench, so a foundry
    // with no test lab still checks mould hardness by hand. `parallel` mirrors
    // moulding so a 2-line plant gets vmh_1/vmh_2 ⇒ two distinct readings series
    // (their tags share ONE manual-entry gateway, so the tag NAME is the only thing
    // separating the lines). The bench VMHD above stays: it and the handheld are
    // different devices that coexist.
    { stageKey: "vmh", parallel: "productionUnits", layer: 5 },
    { stageKey: "vms", parallel: "productionUnits", layer: 5 },
    {
      stageKey: "std_core_setting",
      parallel: "productionUnits",
      layer: 6,
      wires: [{ to: "manual_pour", fromPort: "out_mould", toPort: "in_moulds", materialClass: "green_sand_mould", label: "Cored moulds" }],
    },
    {
      // Pouring by hand from pour ladles (H3, 2026-10-24): the standard line's pour, with its first, last and single pour
      // temperatures and its first car. A line with a pouring machine adds the Auto-Pour Station, a separate stage.
      stageKey: "manual_pour",
      parallel: "productionUnits",
      layer: 7,
      wires: [{ to: "cooling", fromPort: "out_poured", toPort: "in_poured", materialClass: "poured_mould", label: "Poured moulds" }],
    },
    {
      stageKey: "cooling",
      parallel: "productionUnits",
      layer: 8,
      wires: [{ to: "shakeout", fromPort: "out_cooled", toPort: "in_cooled", materialClass: "cooled_mould", label: "Cooled moulds" }],
    },
    {
      // Shakeout (reused ENT_SHAKEOUT) — raw casting + spent return sand.
      stageKey: "shakeout",
      parallel: "productionUnits",
      layer: 9,
      wires: [
        { to: "std_degating", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Raw castings" },
        { to: "std_polygon_sieve", fromPort: "out_sand", toPort: "in_sand", materialClass: "return_sand", label: "Return sand", style: "dashed" },
      ],
    },

    // ── Sand reclamation loop (dashed) ──
    {
      stageKey: "std_polygon_sieve",
      layer: 10,
      wires: [{ to: "std_magnetic_separator", fromPort: "out_sand", toPort: "in_sand", materialClass: "return_sand", label: "Sieved sand", style: "dashed" }],
    },
    {
      stageKey: "std_magnetic_separator",
      layer: 11,
      wires: [{ to: "std_sand_cooler", fromPort: "out_sand", toPort: "in_sand", materialClass: "return_sand", label: "De-tramped sand", style: "dashed" }],
    },
    {
      stageKey: "std_sand_cooler",
      layer: 12,
      wires: [{ to: "std_return_silo", fromPort: "out_sand", toPort: "in_sand", materialClass: "return_sand", label: "Cooled sand", style: "dashed" }],
    },
    {
      stageKey: "std_return_silo",
      layer: 13,
      wires: [
        { to: "sand", fromPort: "out_sand", toPort: "in_return", materialClass: "return_sand", label: "Reclaimed sand", style: "dashed" },
        { to: "std_sand_lab", fromPort: "out_sand", toPort: "in_return", materialClass: "test_specimen", label: "Sand sample" },
      ],
    },
    // std_dust_extractor draws fugitive dust off the AIR around the sand-plant
    // stations (its own oneLine: "bag-house draws fines and dust off the
    // sand-plant stations") — it is not a slipstream of the return_sand
    // conveyor. The old wire from std_sand_cooler (labelled "Dust pickup" but
    // typed return_sand, landing on a port named in_sand) mismodelled it as
    // one: a bag-house is a dry fabric filter, it does not receive or return a
    // bulk sand stream — its out_sand port was correspondingly dead, wired
    // nowhere (#698). Deliberately no material in-edge: a legitimate boundary
    // node collecting from an untracked source (ambient air), same shape as
    // std_raw_material_store originating from "nothing modeled upstream".
    {
      stageKey: "std_dust_extractor",
      layer: 13,
      wires: [{ to: "std_dust_storage", fromPort: "out_dust", toPort: "in_dust", materialClass: "waste", label: "Dust", style: "dashed" }],
    },
    { stageKey: "std_dust_storage", layer: 14 },

    // ── Cast finishing spine ──
    {
      stageKey: "std_degating",
      layer: 10,
      wires: [
        { to: "std_shot_blast", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "De-gated castings" },
        { to: "charge", fromPort: "out_returns", toPort: "in_returns", materialClass: "foundry_return", label: "Runners / risers", style: "dashed" },
      ],
    },
    {
      stageKey: "std_shot_blast",
      layer: 11,
      wires: [{ to: "std_fettling", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Blasted castings" }],
    },
    {
      stageKey: "std_fettling",
      layer: 12,
      wires: [{ to: "std_final_inspection", fromPort: "out_castings", toPort: "in_castings", materialClass: "raw_casting", label: "Fettled castings" }],
    },
    {
      stageKey: "std_final_inspection",
      layer: 13,
      wires: [
        { to: "std_finished_store", fromPort: "out_accepted", toPort: "in_castings", materialClass: "finished_casting", label: "Accepted castings" },
        { to: "tensile", fromPort: "out_specimen", toPort: "in_bars", materialClass: "test_specimen", label: "Test specimen" },
        { to: "charge", fromPort: "out_rejected", toPort: "in_rejects", materialClass: "casting_rejects", label: "Rejects", style: "dashed" },
      ],
    },
    { stageKey: "std_finished_store", layer: 14 },
    { stageKey: "tensile", layer: 14, when: (a) => a.testLab },
  ];
}

/** Charge Yard → Charge Prep — the bought-material boundary (#677): pig iron,
 *  steel scrap and alloy additions ground on a source stage with no input
 *  ports, same layer as charge (a supplier column, not upstream of it — the
 *  established pattern for a sibling source stage; cf. "sand" alongside
 *  "furnace" in ARCH_IRON_GREEN_SAND_AUTO above). */
const STD_CHARGE_YARD_STAGE: ArchetypeStage = {
  stageKey: "charge_yard",
  layer: 0,
  wires: [
    { to: "charge", fromPort: "out_pig", toPort: "in_pig", materialClass: "pig_iron", label: "Pig iron" },
    { to: "charge", fromPort: "out_scrap", toPort: "in_scrap", materialClass: "steel_scrap", label: "Steel scrap" },
    { to: "charge", fromPort: "out_alloy", toPort: "in_alloy", materialClass: "alloy_material", label: "Alloy additions" },
  ],
};

/** Charge Prep → Furnace (shared head of the metal spine). */
const STD_CHARGE_STAGE: ArchetypeStage = {
  stageKey: "charge",
  layer: 0,
  wires: [{ to: "furnace", fromPort: "out_charge", toPort: "in_charge", materialClass: "ferrous_charge", label: "Furnace charge" }],
};

/** Grey-iron melt head — Furnace → Inoculation (plain holding, no Mg) → Pouring. */
const STD_GREY_MELT_STAGES: ArchetypeStage[] = [
  STD_CHARGE_YARD_STAGE,
  STD_CHARGE_STAGE,
  {
    stageKey: "furnace",
    parallel: "meltFurnaces",
    layer: 1,
    wires: [
      { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
      { to: "std_inoculation_ladle", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Molten iron" },
    ],
  },
  { stageKey: "spectro", layer: 2 },
  {
    // Grey iron: the ladle is a plain holding + inoculation ladle (no Mg).
    stageKey: "std_inoculation_ladle",
    layer: 2,
    labelOverride: "Holding / Inoculation Ladle",
    override: {
      oneLine: "A holding and inoculation ladle that holds the molten iron and inoculates it just before pouring.",
      parameters: [{ name: "pour_temperature", unit: "°C", value: 1400, range: [1360, 1450], dataKind: "analog_scalar" }],
    },
    wires: [{ to: "manual_pour", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Inoculated metal (pour ladles)" }],
  },
];

/** Ductile melt head — Furnace → Mg Treatment → Inoculation → Pouring. */
const STD_DUCTILE_MELT_STAGES: ArchetypeStage[] = [
  STD_CHARGE_YARD_STAGE,
  STD_CHARGE_STAGE,
  {
    stageKey: "furnace",
    parallel: "meltFurnaces",
    layer: 1,
    wires: [
      { to: "spectro", fromPort: "out_metal", toPort: "in_sample", materialClass: "molten_iron", label: "Metal sample" },
      { to: "std_treatment_ladle", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Base metal" },
    ],
  },
  { stageKey: "spectro", layer: 2 },
  {
    stageKey: "std_treatment_ladle",
    layer: 2,
    wires: [{ to: "std_inoculation_ladle", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Treated metal" }],
  },
  {
    stageKey: "std_inoculation_ladle",
    layer: 3,
    wires: [{ to: "manual_pour", fromPort: "out_metal", toPort: "in_metal", materialClass: "molten_iron", label: "Inoculated metal (pour ladles)" }],
  },
];

/** Grey iron drops the nodularity readout at final inspection (ductile-only). */
const STD_GREY_FINAL_INSPECTION_OVERRIDE: ArchetypeStage["override"] = {
  oneLine: "Inspects each casting by eye, by non-destructive testing (NDT) and by its microstructure, accepts or rejects it, and records its defects.",
  parameters: [
    { name: "defect_count", value: 4, dataKind: "count_scalar" },
    { name: "rejection_rate", unit: "%", value: 4, range: [0, 20], dataKind: "analog_percentage" },
    { name: "hardness", unit: "BHN", value: 200, range: [130, 300], dataKind: "analog_scalar" },
  ],
};

/** Apply a per-archetype override to a named stage in a fresh common-stage list. */
function withStageOverride(
  stages: ArchetypeStage[],
  stageKey: string,
  override: ArchetypeStage["override"],
): ArchetypeStage[] {
  return stages.map((s) => (s.stageKey === stageKey ? { ...s, override: { ...s.override, ...override } } : s));
}

/** Grey-iron Standard Green-Sand Foundry. */
export const ARCH_GREY_SAND_STANDARD: Archetype = {
  id: "grey-sand-standard",
  name: "Standard Green-Sand Foundry · grey iron",
  blurb:
    "A standard grey iron green sand foundry: charge make-up, induction melting, holding and inoculation, a full sand plant with its reclamation loop, a cold-box core shop, moulding, pouring from ladles, cooling, shakeout, de-gating, shot blasting, fettling, final inspection with non-destructive testing (NDT), a sand lab with an inline sand tester, and the finished casting store.",
  metals: ["grey_iron"],
  route: "green_sand_standard",
  defaultMeltType: "induction",
  defaultVolume: "high",
  productionUnitLabel: "moulding lines",
  defaultCounts: STD_COUNTS,
  uses: STD_USES,
  stages: [
    ...STD_GREY_MELT_STAGES,
    ...withStageOverride(stdCommonStages(), "std_final_inspection", STD_GREY_FINAL_INSPECTION_OVERRIDE),
  ],
};

/** Ductile / SG-iron Standard Green-Sand Foundry (adds Mg treatment + nodularity). */
export const ARCH_DUCTILE_SAND_STANDARD: Archetype = {
  id: "ductile-sand-standard",
  name: "Standard Green-Sand Foundry · ductile (SG) iron",
  blurb:
    "A standard ductile iron green sand foundry: charge make-up, induction melting, magnesium treatment and inoculation, a full sand plant with its reclamation loop, a cold-box core shop, moulding, pouring from ladles, cooling, shakeout, de-gating, shot blasting, fettling, final inspection with non-destructive testing (NDT) and nodularity, a sand lab with an inline sand tester, and the finished casting store.",
  metals: ["ductile_iron"],
  route: "green_sand_standard",
  defaultMeltType: "induction",
  defaultVolume: "high",
  productionUnitLabel: "moulding lines",
  defaultCounts: STD_COUNTS,
  uses: STD_USES,
  stages: [...STD_DUCTILE_MELT_STAGES, ...stdCommonStages()],
};

export const ARCHETYPES: Archetype[] = [
  // The Standard Green-Sand Foundry (~35 stations) is the CANONICAL grey/ductile
  // green-sand template — first here so archForMetal()/ARCHETYPES[0] default a new
  // grey- or ductile-iron plant to it. The legacy 15-station ARCH_IRON_GREEN_SAND_AUTO
  // is retired from the wizard (still exported for any stored-id display lookup).
  ARCH_GREY_SAND_STANDARD,
  ARCH_DUCTILE_SAND_STANDARD,
  ARCH_DUCTILE_NOBAKE,
  ARCH_STEEL_NOBAKE,
  ARCH_AL_HPDC,
  ARCH_AL_GRAVITY,
  ARCH_INVESTMENT,
  ARCH_COPPER_SAND,
  ARCH_ZNMG_HPDC,
];

/** Resolve the best archetype for a metal+route (exact, then route, then first). */
export function archetypeFor(a: { metal: Metal; route: Route }): Archetype {
  return (
    ARCHETYPES.find((x) => x.metals.includes(a.metal) && x.route === a.route) ??
    ARCHETYPES.find((x) => x.route === a.route) ??
    ARCHETYPES[0]
  );
}

export function archetypeById(id: string): Archetype | undefined {
  return ARCHETYPES.find((x) => x.id === id);
}

/** Sensible defaults for an archetype + metal — the wizard's initial answers. */
export function defaultAnswers(arch: Archetype, metal: Metal): BuilderAnswers {
  // Steel + stainless-investment almost always heat-treat (normalise/Q&T,
  // solution-anneal) — default the toggle on so the generated plant matches
  // its own blurb. Other metals leave it optional. (foundry-industry-study §4)
  const heatTreatByDefault =
    arch.id === "steel-nobake" || arch.id === "investment-stainless";
  return {
    metal,
    route: arch.route,
    volume: arch.defaultVolume,
    meltType: arch.defaultMeltType,
    counts: { ...arch.defaultCounts },
    coreShop: arch.defaultCounts.coreMachines > 0,
    heatTreat: heatTreatByDefault,
    machining: false,
    testLab: true,
  };
}
